import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Block as DisableIcon, CheckCircle as EnableIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { PhoneNumberField, matchIsValidTel } from '../components/inputs/PhoneNumberField';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../constants/roles';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import SelectField from '../components/inputs/SelectField';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useFeedback } from '../context/FeedbackContext';
import PermissionSectionsPicker from '../components/users/PermissionSectionsPicker';

const Users = () => {
  const { userRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { confirm, showError, showSuccess } = useFeedback();
  const [users, setUsers] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { loading: submitLoading, run: runSubmit } = useAsyncAction();
  const { run: runRowAction, isPending: isRowPending } = useAsyncAction();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEmployeeContext, setSelectedEmployeeContext] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [useDefaultPermissions, setUseDefaultPermissions] = useState(true);
  const [createUserData, setCreateUserData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: ROLES.EMPLOYEE,
  });
  const [createUserErrors, setCreateUserErrors] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/users');
      const filtered = response.data.filter(
        (user) => user.role === ROLES.MANAGER || user.role === ROLES.EMPLOYEE
      );
      setUsers(filtered);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRole === ROLES.MANAGER) {
      fetchUsers();
    }
  }, [userRole, fetchUsers]);

  const fetchAvailablePermissions = async () => {
    try {
      const response = await axios.get('/api/users/permissions/available');
      if (response.data && response.data.permissions && Array.isArray(response.data.permissions)) {
        setAvailablePermissions(response.data.permissions);
        return response.data.permissions;
      } else {
        console.error('Invalid permissions response:', response.data);
        setAvailablePermissions([]);
        return [];
      }
    } catch (error) {
      console.error('Failed to fetch available permissions:', error);
      console.error('Error details:', error.response?.data);
      setAvailablePermissions([]);
      throw error;
    }
  };

  const validateCreateUser = () => {
    const err = {};
    const email = (createUserData.email || '').trim();
    if (!email) err.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) err.email = 'Enter a valid email address';
    if (!(createUserData.firstName || '').trim()) err.firstName = 'First name is required';
    if (!(createUserData.lastName || '').trim()) err.lastName = 'Last name is required';
    if (!createUserData.role) err.role = 'Role is required';
    const phone = (createUserData.phone || '').trim();
    if (!phone) err.phone = 'Phone is required';
    else if (!matchIsValidTel(phone)) err.phone = 'Enter a valid phone number';
    setCreateUserErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleCreateUser = async () => {
    if (!validateCreateUser()) return;
    await runSubmit(async () => {
      try {
        const phoneTrim = (createUserData.phone || '').trim();
        await axios.post('/api/users', {
          email: (createUserData.email || '').trim().toLowerCase(),
          firstName: (createUserData.firstName || '').trim(),
          lastName: (createUserData.lastName || '').trim(),
          phone: phoneTrim,
          role: createUserData.role,
        });
        setCreateDialogOpen(false);
        setCreateUserData({
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          role: ROLES.EMPLOYEE,
        });
        setCreateUserErrors({});
        fetchUsers();
        showSuccess(t('users.userCreated'));
      } catch (error) {
        console.error('Failed to create user:', error);
        showError(error.response?.data?.error || 'Failed to create user');
      }
    });
  };

  const handlePermissionToggle = (permission) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter(p => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  const handleSectionToggle = (sectionPermissions) => {
    const allSelected = sectionPermissions.every((p) => selectedPermissions.includes(p));
    if (allSelected) {
      setSelectedPermissions(selectedPermissions.filter((p) => !sectionPermissions.includes(p)));
    } else {
      const merged = new Set([...selectedPermissions, ...sectionPermissions]);
      setSelectedPermissions([...merged]);
    }
  };

  const handleSavePermissions = async () => {
    await runSubmit(async () => {
      try {
        const permissions = useDefaultPermissions ? [] : selectedPermissions;
        await axios.put(`/api/users/${selectedUser._id}/permissions`, { permissions });
        setPermissionsDialogOpen(false);
        fetchUsers();
        showSuccess(t('users.permissionsUpdated'));
      } catch (error) {
        console.error('Failed to save permissions:', error);
        showError(error.response?.data?.error || 'Failed to save permissions');
      }
    });
  };

  const handleEditOpen = async (user) => {
    let detail = user;
    try {
      const { data } = await axios.get(`/api/users/${user._id}`);
      detail = data;
    } catch (e) {
      console.error(e);
    }
    setSelectedUser(detail);
    setEditUserData({
      firstName: detail.firstName,
      lastName: detail.lastName,
      email: detail.email,
      phone: detail.phone || '',
    });

    const isEmployee = detail.role === ROLES.EMPLOYEE;
    setSelectedEmployeeContext(isEmployee);

    if (isEmployee) {
      if (detail.customPermissions && detail.customPermissions.length > 0) {
        setSelectedPermissions(detail.customPermissions);
        setUseDefaultPermissions(false);
      } else {
        setSelectedPermissions([]);
        setUseDefaultPermissions(true);
      }
      try {
        await fetchAvailablePermissions();
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    }

    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    const phoneTrim = (editUserData.phone || '').trim();
    if (!phoneTrim || !matchIsValidTel(phoneTrim)) {
      showError(t('users.invalidPhone'));
      return;
    }
    await runSubmit(async () => {
      try {
        await axios.put(`/api/users/${selectedUser._id}`, {
          ...editUserData,
          phone: phoneTrim,
        });

        if (selectedEmployeeContext && selectedUser?.role === ROLES.EMPLOYEE) {
          const permissions = useDefaultPermissions ? [] : selectedPermissions;
          await axios.put(`/api/users/${selectedUser._id}/permissions`, { permissions });
        }

        setEditDialogOpen(false);
        fetchUsers();
      } catch (error) {
        console.error('Failed to update user:', error);
        showError(error.response?.data?.error || 'Failed to update user');
      }
    });
  };

  const handleToggleDisable = async (user) => {
    const action = user.disabled ? 'enable' : 'disable';
    const name = `${user.firstName} ${user.lastName}`.trim();
    if (!(await confirm({
      message: t('users.confirmToggleUser', { action, name }),
      confirmColor: user.disabled ? 'primary' : 'warning',
    }))) return;
    const key = `toggle-${user._id}`;
    await runRowAction(async () => {
      try {
        await axios.patch(`/api/users/${user._id}/toggle-disable`);
        fetchUsers();
      } catch (error) {
        console.error('Failed to toggle user status:', error);
        showError(error.response?.data?.error || 'Failed to update user status');
      }
    }, key);
  };

  const handleDeleteUser = async (user) => {
    const name = `${user.firstName} ${user.lastName}`.trim();
    if (!(await confirm({
      message: t('users.confirmDeleteUser', { name }),
      confirmColor: 'error',
      confirmLabel: t('users.delete'),
    }))) return;
    const key = `delete-${user._id}`;
    await runRowAction(async () => {
      try {
        await axios.delete(`/api/users/${user._id}`);
        fetchUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        showError(error.response?.data?.error || 'Failed to delete user');
      }
    }, key);
  };

  if (userRole !== ROLES.MANAGER) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Alert severity="error">{t('users.noPermission')}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }}>
            {t('users.title')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setCreateUserData({
                email: '',
                firstName: '',
                lastName: '',
                phone: '',
                role: ROLES.EMPLOYEE,
              });
              setCreateDialogOpen(true);
            }}
          >
            {t('users.createUser')}
          </Button>
        </Box>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {users.map((user) => {
              const displayRole = user.role || '—';
              return (
                <Paper key={user._id} variant="outlined" sx={{ p: 2, opacity: user.disabled ? 0.6 : 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="subtitle2" fontWeight={600}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Chip label={displayRole} size="small" color={displayRole === ROLES.MANAGER ? 'primary' : 'default'} />
                        {user.disabled && <Chip label={t('users.disabled')} size="small" color="error" variant="outlined" />}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{user.email}</Typography>
                      {user.phone && <Typography variant="body2" color="text.secondary">{user.phone}</Typography>}
                    </Box>
                    <Box display="flex" gap={0.5} flexShrink={0}>
                      <Tooltip title={t('users.edit')}>
                        <IconButton size="small" color="primary" onClick={() => handleEditOpen(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.disabled ? t('users.enable') : t('users.disable')}>
                        <span>
                          <IconButton size="small" color={user.disabled ? 'success' : 'warning'} onClick={() => handleToggleDisable(user)} disabled={isRowPending(`toggle-${user._id}`)}>
                            {user.disabled ? <EnableIcon fontSize="small" /> : <DisableIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('users.delete')}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)} disabled={isRowPending(`delete-${user._id}`)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            {users.length === 0 && !loading && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>{t('common.noResults')}</Typography>
            )}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('users.name')}</TableCell>
                  <TableCell>{t('users.email')}</TableCell>
                  <TableCell>{t('users.phone')}</TableCell>
                  <TableCell>{t('users.role')}</TableCell>
                  <TableCell>{t('users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const displayRole = user.role || '—';
                  return (
                    <TableRow key={user._id} sx={user.disabled ? { opacity: 0.5 } : {}}>
                      <TableCell>
                        {user.firstName} {user.lastName}
                        {user.disabled && <Chip label={t('users.disabled')} size="small" color="error" variant="outlined" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={displayRole}
                          size="small"
                          color={displayRole === ROLES.MANAGER ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} alignItems="center">
                          <Tooltip title={t('users.edit')}>
                            <IconButton size="small" color="primary" onClick={() => handleEditOpen(user)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={user.disabled ? t('users.enable') : t('users.disable')}>
                            <span>
                              <IconButton size="small" color={user.disabled ? 'success' : 'warning'} onClick={() => handleToggleDisable(user)} disabled={isRowPending(`toggle-${user._id}`)}>
                                {user.disabled ? <EnableIcon fontSize="small" /> : <DisableIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('users.delete')}>
                            <span>
                              <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)} disabled={isRowPending(`delete-${user._id}`)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog 
          open={createDialogOpen} 
          onClose={() => { setCreateDialogOpen(false); setCreateUserErrors({}); }} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>{t('users.createNewUser')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('users.email')}
              type="email"
              value={createUserData.email}
              onChange={(e) => {
                setCreateUserData({ ...createUserData, email: e.target.value });
                if (createUserErrors.email) setCreateUserErrors({ ...createUserErrors, email: '' });
              }}
              margin="normal"
              required
              error={!!createUserErrors.email}
              helperText={createUserErrors.email}
              autoComplete="email"
            />
            <TextField
              fullWidth
              label={t('users.firstName')}
              value={createUserData.firstName}
              onChange={(e) => {
                setCreateUserData({ ...createUserData, firstName: e.target.value });
                if (createUserErrors.firstName) setCreateUserErrors({ ...createUserErrors, firstName: '' });
              }}
              margin="normal"
              required
              error={!!createUserErrors.firstName}
              helperText={createUserErrors.firstName}
              autoComplete="given-name"
            />
            <TextField
              fullWidth
              label={t('users.lastName')}
              value={createUserData.lastName}
              onChange={(e) => {
                setCreateUserData({ ...createUserData, lastName: e.target.value });
                if (createUserErrors.lastName) setCreateUserErrors({ ...createUserErrors, lastName: '' });
              }}
              margin="normal"
              required
              error={!!createUserErrors.lastName}
              helperText={createUserErrors.lastName}
              autoComplete="family-name"
            />
            <PhoneNumberField
              label={t('users.phone')}
              value={createUserData.phone}
              onChange={(value) => {
                setCreateUserData({ ...createUserData, phone: value });
                if (createUserErrors.phone) setCreateUserErrors({ ...createUserErrors, phone: '' });
              }}
              error={!!createUserErrors.phone}
              helperText={createUserErrors.phone || 'Country code and number; stored in international format'}
            />
            <SelectField
              label={t('users.role')}
              value={createUserData.role}
              onChange={(next) => {
                setCreateUserData({ ...createUserData, role: next });
                if (createUserErrors.role) setCreateUserErrors({ ...createUserErrors, role: '' });
              }}
              options={[
                { value: ROLES.MANAGER, label: ROLES.MANAGER },
                { value: ROLES.EMPLOYEE, label: ROLES.EMPLOYEE },
              ]}
              error={!!createUserErrors.role}
              helperText={createUserErrors.role || ''}
              fullWidth
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={submitLoading}>{t('users.cancel')}</Button>
            <Button onClick={handleCreateUser} variant="contained" disabled={submitLoading}>
              {submitLoading ? t('common.creating') : t('users.create')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>{t('users.editUser')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('users.firstName')}
              value={editUserData.firstName}
              onChange={(e) => setEditUserData({ ...editUserData, firstName: e.target.value })}
              margin="normal"
              autoComplete="given-name"
            />
            <TextField
              fullWidth
              label={t('users.lastName')}
              value={editUserData.lastName}
              onChange={(e) => setEditUserData({ ...editUserData, lastName: e.target.value })}
              margin="normal"
              autoComplete="family-name"
            />
            <TextField
              fullWidth
              label={t('users.email')}
              type="email"
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              margin="normal"
              autoComplete="email"
            />
            <PhoneNumberField
              label={t('users.phone')}
              value={editUserData.phone}
              onChange={(value) => setEditUserData({ ...editUserData, phone: value })}
              helperText="Country code and number; stored in international format"
            />

            {selectedEmployeeContext && selectedUser?.role === ROLES.EMPLOYEE && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('users.permissions')}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useDefaultPermissions}
                      onChange={(e) => {
                        setUseDefaultPermissions(e.target.checked);
                        if (e.target.checked) {
                          setSelectedPermissions([]);
                        }
                      }}
                    />
                  }
                  label={t('users.useDefaultPermissions')}
                  sx={{ mb: 1 }}
                />

                {!useDefaultPermissions && (
                  <>
                    <PermissionSectionsPicker
                      availablePermissions={availablePermissions}
                      selectedPermissions={selectedPermissions}
                      onTogglePermission={handlePermissionToggle}
                      onToggleSection={handleSectionToggle}
                      emptyMessage={t('users.noPermissions')}
                    />
                    {selectedPermissions.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {t('users.permissionsSelected', { count: selectedPermissions.length })}
                      </Typography>
                    )}
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={submitLoading}>{t('users.cancel')}</Button>
            <Button onClick={handleEditSave} variant="contained" disabled={submitLoading}>
              {submitLoading ? t('common.saving') : t('users.save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={permissionsDialogOpen}
          onClose={() => setPermissionsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            {t('users.managePermissionsTitle', {
              name: `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim(),
            })}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('users.permissionsDialogHelp')}
            </Alert>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={useDefaultPermissions}
                  onChange={(e) => {
                    setUseDefaultPermissions(e.target.checked);
                    if (e.target.checked) {
                      setSelectedPermissions([]);
                    }
                  }}
                />
              )}
              label={t('users.useDefaultPermissions')}
              sx={{ mb: 2 }}
            />

            {!useDefaultPermissions && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  {t('users.selectCustomPermissions')}
                </Typography>
                <PermissionSectionsPicker
                  availablePermissions={availablePermissions}
                  selectedPermissions={selectedPermissions}
                  onTogglePermission={handlePermissionToggle}
                  onToggleSection={handleSectionToggle}
                  emptyMessage={loading ? t('users.loadingPermissions') : t('users.noPermissions')}
                />
                {selectedPermissions.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    {t('users.permissionsSelected', { count: selectedPermissions.length })}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermissionsDialogOpen(false)} disabled={submitLoading}>
              {t('users.cancel')}
            </Button>
            <Button onClick={handleSavePermissions} variant="contained" disabled={submitLoading}>
              {submitLoading ? t('common.saving') : t('users.savePermissions')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Users;
